import firebase from "firebase";
import types from "types";
import moment from "moment";
import { isForStatement } from "typescript";

const colors = {
  gray: {
    100: "#f6f9fc",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#8898aa",
    700: "#525f7f",
    800: "#32325d",
    900: "#212529",
  },
  theme: {
    default: "#172b4d",
    primary: "#5e72e4",
    secondary: "#f4f5f7",
    info: "#11cdef",
    success: "#2dce89",
    danger: "#f5365c",
    warning: "#fb6340",
  },
  black: "#12263F",
  white: "#FFFFFF",
  transparent: "transparent",
};

export const getLocationsAndChecklists = () => async (dispatch, getState) => {
  const userState = getState().user;
  const db = firebase.firestore();
  const franchisesRef = db.collection("franchises");
  dispatch({
    type: types.FETCHING_FRANCHISES,
  });

  try {
    const franchises = {};
    if (userState.user.role === "owner") {
      userState.user.franchises.forEach(franchise => {
        franchises[franchise] = {};
      });
    } else {
      const snapshot = await franchisesRef.get();
      snapshot.forEach(doc => {
        franchises[doc.id] = {};
      });
    }
    const franchisesNames = Object.keys(franchises);

    const getFranchiseChecklistsAndLocations = async franchise => {
      try {
        const generalChecklistsSnapshot =
          userState.user.role === "owner"
            ? await franchisesRef
                .doc(franchise)
                .collection("generalChecklists")
                .where("enviroMasterChecklist", "==", false)
                .get()
            : await franchisesRef
                .doc(franchise)
                .collection("generalChecklists")
                .get();
        const locationsSnapshot =
          userState.user.role === "owner"
            ? await franchisesRef
                .doc(franchise)
                .collection("locations")
                .where("owner", "==", userState.user.uid)
                .get()
            : await franchisesRef.doc(franchise).collection("locations").get();
        const generalChecklists = [];
        const locations = [];

        generalChecklistsSnapshot.forEach(doc => {
          const checklist = doc.data();
          checklist.name = doc.id;
          generalChecklists.push(checklist);
        });

        locationsSnapshot.forEach(doc => {
          const location = doc.data();
          location.address = doc.id;
          locations.push(location);
        });
        return {
          generalChecklists,
          locations,
        };
      } catch (error) {
        dispatch({
          type: types.ERROR_FETCHING_FRANCHISES,
          payload: error,
        });
        throw new Error(`error getFranchiseChecklistsAndLocations: ${error}`);
      }
    };

    const franchisesProperties = await Promise.all(
      franchisesNames.map(franchise =>
        getFranchiseChecklistsAndLocations(franchise)
      )
    );
    const locationSpecificChecklistsSnapshots = await Promise.all(
      franchisesProperties.map(async (franchise, index) => {
        try {
          return await Promise.all(
            franchise.locations.map(
              location =>
                new Promise(resolve =>
                  resolve(
                    franchisesRef
                      .doc(franchisesNames[index])
                      .collection("locations")
                      .doc(location.address)
                      .collection("locationSpecificChecklists")
                      .get()
                  )
                )
            )
          );
        } catch (error) {
          console.error(error);
        }
      })
    );

    franchisesNames.forEach((franchise, index) => {
      franchises[franchise].locations = franchisesProperties[index].locations;
      franchises[franchise].generalChecklists =
        franchisesProperties[index].generalChecklists;
    });

    locationSpecificChecklistsSnapshots.forEach(
      (franchiseArray, franchiseIndex) => {
        franchiseArray.forEach((locationArray, locationIndex) => {
          franchises[franchisesNames[franchiseIndex]].locations[
            locationIndex
          ].locationSpecificChecklists = [];
          locationArray.forEach(doc => {
            const checklist = doc.data();
            checklist.name = doc.id;
            franchises[franchisesNames[franchiseIndex]].locations[
              locationIndex
            ].locationSpecificChecklists.push(checklist);
          });
        });
      }
    );
    dispatch({
      type: types.FRANCHISES_FETCHED,
      payload: franchises,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_FETCHING_FRANCHISES,
      payload: error,
    });
    throw new Error(`Error in getLocationsAndChecklists: ${error}`);
  }
};

export const getPastChecklists = (date, franchise = null, address = null) => async (dispatch, getState) => {
  const state = getState().checklists;
  const filter = getState().filter.filter;
  const user = getState().user.user;
  const startDate = date
    ? moment(date).format("YYYY-MM-DD")
    : moment().clone().startOf("month").format("YYYY-MM-DD");
  const endDate = date
    ? moment(date).add(1, "months").format("YYYY-MM-DD")
    : moment().clone().startOf("month").add(1, "month").format("YYYY-MM-DD");
  if (state.limitReached) return;
  if (!franchise && !address) {
    franchise = filter.franchise;
    address = filter.address;
  }

  if (!address) return;
  if (
    state.pastChecklists &&
    state.pastChecklists[`${franchise}%${address}`] &&
    Object.keys(
      state.pastChecklists[`${franchise}%${address}`]
    ).includes(startDate)
  )
    return;

  const db = firebase.firestore();
  const techniciansChecklistsRef = db
    .collection("franchises")
    .doc(franchise)
    .collection("locations")
    .doc(address)
    .collection("pastChecklistsTechnicians");
  const managersChecklistsRef = db
    .collection("franchises")
    .doc(franchise)
    .collection("locations")
    .doc(address)
    .collection("pastChecklistsManagers");
  dispatch({
    type: types.FETCHING_PAST_CHECKLISTS,
  });
  try {
    let snapshot = {};
    if (user.role !== "owner") {
      const techniciansSnapshot = await techniciansChecklistsRef
        .orderBy("date", "asc")
        .startAt(startDate)
        .endBefore(endDate)
        .get();

      const managersSnapshot = await managersChecklistsRef
        .orderBy("date", "asc")
        .startAt(startDate)
        .endBefore(endDate)
        .get();

      techniciansSnapshot.forEach(doc => {
        snapshot[doc.id] = doc.data();
      });

      managersSnapshot.forEach(doc => {
        Object.keys(doc.data()).forEach(checklist => {
          snapshot[doc.id][checklist] = doc.data()[checklist];
        });
      });
    } else {
      const managersSnapshot = await managersChecklistsRef
        .orderBy("date", "asc")
        .startAt(startDate)
        .endBefore(endDate)
        .get();

      managersSnapshot.forEach(doc => {
        snapshot[doc.id] = doc.data();
      });
    }

    if (!state.pastChecklists) {
      return dispatch({
        type: types.PAST_CHECKLISTS_FETCHED,
        payload: {
          [`${franchise}%${address}`]: snapshot,
        },
      });
    }

    return dispatch({
      type: types.ADD_PAST_CHECKLISTS,
      payload: {
        [`${franchise}%${address}`]: snapshot,
      },
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_FETCHING_PAST_CHECKLISTS,
      payload: error,
    });
    throw new Error(`Error in getPastChecklists: ${error}`);
  }
};

export const createCalendarEvents = () => (dispatch, getState) => {
  const filter = getState().filter.filter;
  const state = getState().checklists;
  try {
    const data = {
      ...state.pastChecklists[`${filter.franchise}%${filter.address}`],
    };
    if (data) {
      const calendar = [];
      Object.keys(data).forEach(date => {
        delete data[date]["date"];
        Object.keys(data[date]).forEach((checklist, index) => {
          const currentChecklist = data[date][checklist];
          calendar.push({
            id: date + checklist + index,
            title: checklist,
            start: moment(date)._d,
            allDay: true,
            description: `${
              currentChecklist.completionTime
                ? `This checklist was completed by ${
                    state.users[currentChecklist.completedBy].name
                  } @ ${currentChecklist.completionTime}. It was completed ${
                    currentChecklist.onTime ? "on time" : "late"
                  } and it ${
                    currentChecklist.passed ? "passed" : "didn't pass"
                  }.`
                : "This checklist was not completed"
            }.`,
            className: currentChecklist.completionTime
              ? currentChecklist.onTime && currentChecklist.passed
                ? "bg-success"
                : "bg-yellow"
              : "bg-danger",
          });
        });
      });
      dispatch({
        type: types.SET_CALENDAR,
        payload: calendar.sort((a,b) => a.start - b.start),
      });
    }
  } catch (error) {
    throw new Error(`Error creating the calendar events ${error}`);
  }
};

export const createUserCalendarEvents = (uid) => (dispatch, getState) => {
  const checklistsState = getState().checklists;
  const calendar = [];
  checklistsState.users[uid].assignedLocations.forEach(location => {
    const data = {
      ...checklistsState.pastChecklists[location],
    };
    if (data) {
      Object.keys(data).forEach(date => {
        delete data[date]["date"];
        Object.keys(data[date]).forEach((checklist, index) => {
          const currentChecklist = data[date][checklist];
          if (currentChecklist.completedBy !== uid) return;
          calendar.push({
            id: date + checklist + index,
            title: location.split('%').join(' | ') + ': ' + checklist,
            start: moment(date)._d,
            allDay: true,
            description: `${
              currentChecklist.completionTime
                ? `This checklist was completed by ${
                    checklistsState.users[currentChecklist.completedBy].name
                  } @ ${currentChecklist.completionTime}. It was completed ${
                    currentChecklist.onTime ? "on time" : "late"
                  } and it ${
                    currentChecklist.passed ? "passed" : "didn't pass"
                  }.`
                : "This checklist was not completed"
            }.`,
            className: currentChecklist.completionTime
              ? currentChecklist.onTime && currentChecklist.passed
                ? "bg-success"
                : "bg-yellow"
              : "bg-danger",
          });
        });
      });
    }
  })
  dispatch({
    type: types.SET_CALENDAR,
    payload: calendar.sort((a,b) => a.start - b.start),
  });
}

export const setPastChecklists = pastChecklists => dispatch => {
  dispatch({
    type: types.PAST_CHECKLISTS_FETCHED,
    payload: pastChecklists,
  });
};

export const setLimitReached = () => dispatch => {
  dispatch({
    type: types.SET_LIMIT_REACHED,
  });
};

export const getPerformanceData = () => async (dispatch, getState) => {
  dispatch({
    type: types.FETCHING_PERFORMANCE_DATA,
  });
  const user = getState().user.user;
  const state = getState().checklists;
  const filter = getState().filter.filter;
  let snapshot;
  try {
    if (!filter.franchise) {
      if (Object.keys(state.performance).length > 0)
        return dispatch({
          type: types.DATA_ALREADY_EXISTS,
        });
      dispatch({
        type: types.FETCHING_PERFORMANCE_DATA,
      });
      if (user.role === "owner") {
        const userLocations = [];
        Object.keys(state.franchises).forEach(franchise => {
          state.franchises[franchise].locations.forEach(location => {
            userLocations.push(`${franchise}%${location.address}%Managers`);
          });
        });
        snapshot = await Promise.all(
          userLocations.map(
            async location =>
              await firebase
                .firestore()
                .collection("checklists")
                .doc(location)
                .get()
          )
        );
        userLocations.forEach((location, index) => {
          dispatch({
            type: types.PERFORMANCE_DATA_FETCHED,
            payload: [
              location.split("%")[0] + "%" + location.split("%")[1],
              snapshot[index].data(),
            ],
          });
        });
      } else {
        snapshot = await firebase
          .firestore()
          .collection("checklists")
          .doc("aggregated")
          .get();
        dispatch({
          type: types.PERFORMANCE_DATA_FETCHED,
          payload: ["aggregated", snapshot.data()],
        });
      }
    } else {
      if (
        Object.keys(state.performance)
          .map(location => location.split("%")[0])
          .includes(filter.franchise)
      )
        return dispatch({
          type: types.DATA_ALREADY_EXISTS,
        });
      dispatch({
        type: types.FETCHING_PERFORMANCE_DATA,
      });
      const managersLocations = [];
      state.franchises[filter.franchise].locations.forEach(location => {
        managersLocations.push(
          `${filter.franchise}%${location.address}%Managers`
        );
      });
      const techniciansLocations = [];
      state.franchises[filter.franchise].locations.forEach(location => {
        techniciansLocations.push(
          `${filter.franchise}%${location.address}%Technicians`
        );
      });

      const managersSnapshot = await Promise.all(
        managersLocations.map(
          async location =>
            await firebase
              .firestore()
              .collection("checklists")
              .doc(location)
              .get()
        )
      );
      const techniciansSnapshot = await Promise.all(
        techniciansLocations.map(
          async location =>
            await firebase
              .firestore()
              .collection("checklists")
              .doc(location)
              .get()
        )
      );

      managersLocations.forEach((location, index) => {
        const totalPerformance = {};
        if (managersSnapshot[index].data()) Object.keys(managersSnapshot[index].data()).forEach(day => {
          totalPerformance[day] = {
            checklistsCompleted:
              managersSnapshot[index].data()[day].checklistsCompleted +
              (techniciansSnapshot[index].data()[day] ? techniciansSnapshot[index].data()[day].checklistsCompleted : 0),
            passed:
              managersSnapshot[index].data()[day].passed +
              (techniciansSnapshot[index].data()[day] ? techniciansSnapshot[index].data()[day].passed : 0),
            totalChecklists:
              managersSnapshot[index].data()[day].totalChecklists +
              (techniciansSnapshot[index].data()[day] ? techniciansSnapshot[index].data()[day].totalChecklists : 0),
            completedOnTime:
              managersSnapshot[index].data()[day].completedOnTime +
              (techniciansSnapshot[index].data()[day] ? techniciansSnapshot[index].data()[day].completedOnTime : 0),

            highSatisfaction: managersSnapshot[index].data()[day].highSatisfaction,
            mediumSatisfaction: managersSnapshot[index].data()[day].mediumSatisfaction,
            lowSatisfaction: managersSnapshot[index].data()[day].lowSatisfaction,
            floorsDirtyLow: managersSnapshot[index].data()[day].floorsDirtyLow,
            paperTowelsDispenserEmptyLow: managersSnapshot[index].data()[day].paperTowelsDispenserEmptyLow,
            sinksDirtyLow: managersSnapshot[index].data()[day].sinksDirtyLow,
            soapDispenserEmptyLow: managersSnapshot[index].data()[day].soapDispenserEmptyLow,
            toiletPaperDispenserEmptyLow: managersSnapshot[index].data()[day].toiletPaperDispenserEmptyLow,
            toiletsDirtyLow: managersSnapshot[index].data()[day].toiletsDirtyLow,

            floorsDirtyMedium: managersSnapshot[index].data()[day].floorsDirtyMedium,
            paperTowelsDispenserEmptyMedium: managersSnapshot[index].data()[day].paperTowelsDispenserEmptyMedium,
            sinksDirtyMedium: managersSnapshot[index].data()[day].sinksDirtyMedium,
            soapDispenserEmptyMedium: managersSnapshot[index].data()[day].soapDispenserEmptyMedium,
            toiletPaperDispenserEmptyMedium: managersSnapshot[index].data()[day].toiletPaperDispenserEmptyMedium,
            toiletsDirtyMedium: managersSnapshot[index].data()[day].toiletsDirtyMedium,
          };
        });
        dispatch({
          type: types.PERFORMANCE_DATA_FETCHED,
          payload: [
            location.split("%")[0] + "%" + location.split("%")[1],
            totalPerformance,
          ],
        });
      });
    }
  } catch (error) {
    dispatch({
      type: types.ERROR_FETCHING_PERFORMANCE_DATA,
      payload: error,
    });
    throw new Error(`Error getting performance data ${error}`);
  }
};

export const createDataStructure = () => (dispatch, getState) => {
  try {
    const state = getState().checklists;
    const filter = getState().filter.filter;
    const timeFrame = getState().filter.timeFrame;
    const chartLabels =
      timeFrame === "week"
        ? [
            moment().subtract(7, "days").format("MM-DD-YYYY"),
            moment().subtract(6, "days").format("MM-DD-YYYY"),
            moment().subtract(5, "days").format("MM-DD-YYYY"),
            moment().subtract(4, "days").format("MM-DD-YYYY"),
            moment().subtract(3, "days").format("MM-DD-YYYY"),
            moment().subtract(2, "days").format("MM-DD-YYYY"),
            moment().subtract(1, "days").format("MM-DD-YYYY"),
          ]
        : timeFrame === "month"
        ? [
            `from ${moment()
              .subtract(28, "days")
              .format("MM-DD-YYYY")} to ${moment()
              .subtract(21, "days")
              .format("MM-DD-YYYY")}`,
            `from ${moment()
              .subtract(20, "days")
              .format("MM-DD-YYYY")} to ${moment()
              .subtract(14, "days")
              .format("MM-DD-YYYY")}`,
            `from ${moment()
              .subtract(13, "days")
              .format("MM-DD-YYYY")} to ${moment()
              .subtract(7, "days")
              .format("MM-DD-YYYY")}`,
            `from ${moment()
              .subtract(6, "days")
              .format("MM-DD-YYYY")} to ${moment()
              .subtract(1, "days")
              .format("MM-DD-YYYY")}`,
          ]
        : [
            moment().subtract(11, "months").format("MMM"),
            moment().subtract(10, "months").format("MMM"),
            moment().subtract(9, "months").format("MMM"),
            moment().subtract(8, "months").format("MMM"),
            moment().subtract(7, "months").format("MMM"),
            moment().subtract(6, "months").format("MMM"),
            moment().subtract(5, "months").format("MMM"),
            moment().subtract(4, "months").format("MMM"),
            moment().subtract(3, "months").format("MMM"),
            moment().subtract(2, "months").format("MMM"),
            moment().subtract(1, "months").format("MMM"),
            moment().subtract(0, "months").format("MMM"),
          ];

    const totalChecklists = Array(chartLabels.length).fill(0);
    const completedOnTime = Array(chartLabels.length).fill(0);
    const checklistsPassed = Array(chartLabels.length).fill(0);
    const completed = Array(chartLabels.length).fill(0);
    const userFeedback = [0, 0, 0];
    const additionalFeedback= {
      low: {
        "Floors dirty": 0,
        "Paper towels dispenser empty": 0,
        "Sinks dirty": 0,
        "Soap dispenser empty": 0,
        "Toilet paper dispenser empty": 0,
        "Toilets dirty": 0,
      },
      medium: {
        "Floors dirty": 0,
        "Paper towels dispenser empty": 0,
        "Sinks dirty": 0,
        "Soap dispenser empty": 0,
        "Toilet paper dispenser empty": 0,
        "Toilets dirty": 0,
      },
      totalFeedback: 0,
    };
    const calendar = [];
    let calendarFiltered;
    if (Object.keys(state.performance).length > 0) {
      // First, we will add all the performance depending on the filter applied:
      let performance = {};
      if (!filter.franchise) {
        if (Object.keys(state.performance).includes("aggregated")) {
          performance = { ...state.performance["aggregated"] };
        } else {
          Object.keys(state.performance).forEach(location => {
            if (!state.performance[location]) return;
            Object.keys(state.performance[location]).forEach(day => {
              if (!performance[day]) performance[day] = {};
              Object.keys(state.performance[location][day]).forEach(key => {
                if (!performance[day][key]) {
                  performance[day][key] = state.performance[location][day][key];
                } else {
                  performance[day][key] +=
                    state.performance[location][day][key];
                }
              });
            });
          });
        }
      } else if (!filter.address) {
        Object.keys(state.performance).forEach(location => {
          if (!state.performance[location]) return;
          if (location.split("%")[0] !== filter.franchise) return;
          Object.keys(state.performance[location]).forEach(day => {
            if (!performance[day]) performance[day] = {};
            Object.keys(state.performance[location][day]).forEach(key => {
              if (!performance[day][key]) {
                performance[day][key] = state.performance[location][day][key];
              } else {
                performance[day][key] += state.performance[location][day][key];
              }
            });
          });
        });
      } else {
        performance =
          state.performance[`${filter.franchise}%${filter.address}`] ? state.performance[`${filter.franchise}%${filter.address}`] : {};
      }
      Object.keys(performance).forEach((date, dayIndex) => {
        let propertiesIndex = null;
        if (timeFrame === "week") {
          if (date < moment().subtract(7, "days").format("YYYY-MM-DD")) return;
          propertiesIndex =
            chartLabels.length -
            moment().startOf("day").diff(moment(date), "days");
        } else if (timeFrame === "month") {
          if (moment().startOf("day").diff(moment(date), "days") < 8) {
            propertiesIndex = chartLabels.length - 1;
          } else if (moment().startOf("day").diff(moment(date), "days") < 15) {
            propertiesIndex = chartLabels.length - 2;
          } else if (moment().startOf("day").diff(moment(date), "days") < 22) {
            propertiesIndex = chartLabels.length - 3;
          } else if (moment().startOf("day").diff(moment(date), "days") < 29) {
            propertiesIndex = chartLabels.length - 4;
          } else {
            return;
          }
        } else {
          propertiesIndex =
            chartLabels.length -
            moment().startOf("day").diff(moment(date), "months") -
            1;
        }

        totalChecklists[propertiesIndex] += performance[date].totalChecklists || 0;
        completedOnTime[propertiesIndex] += performance[date].completedOnTime || 0;
        checklistsPassed[propertiesIndex] += performance[date].passed || 0;
        completed[propertiesIndex] += performance[date].checklistsCompleted || 0;
        userFeedback[0] += performance[date].highSatisfaction || 0;
        userFeedback[1] += performance[date].mediumSatisfaction || 0;
        userFeedback[2] += performance[date].lowSatisfaction || 0;

        additionalFeedback.low["Floors dirty"] += performance[date].floorsDirtyLow || 0;
        additionalFeedback.low["Paper towels dispenser empty"] += performance[date].paperTowelsDispenserEmptyLow || 0;
        additionalFeedback.low["Sinks dirty"] += performance[date].sinksDirtyLow || 0;
        additionalFeedback.low["Soap dispenser empty"] += performance[date].soapDispenserEmptyLow || 0;
        additionalFeedback.low["Toilet paper dispenser empty"] += performance[date].toiletPaperDispenserEmptyLow || 0;
        additionalFeedback.low["Toilets dirty"] += performance[date].toiletsDirtyLow || 0;

        additionalFeedback.medium["Floors dirty"] += performance[date].floorsDirtyMedium || 0;
        additionalFeedback.medium["Paper towels dispenser empty"] += performance[date].paperTowelsDispenserEmptyMedium || 0;
        additionalFeedback.medium["Sinks dirty"] += performance[date].sinksDirtyMedium || 0;
        additionalFeedback.medium["Soap dispenser empty"] += performance[date].soapDispenserEmptyMedium || 0;
        additionalFeedback.medium["Toilet paper dispenser empty"] += performance[date].toiletPaperDispenserEmptyMedium || 0;
        additionalFeedback.medium["Toilets dirty"] += performance[date].toiletsDirtyMedium || 0;

        additionalFeedback.totalFeedback += (performance[date].highSatisfaction || 0) + (performance[date].mediumSatisfaction || 0) + (performance[date].lowSatisfaction || 0);
      });
    }
    const checklistsCompletedChart = {
      labels: chartLabels,
      datasets: [
        {
          label: "Performance",
          data: totalChecklists.map((item, index) => {
            if (completed[index] === 0) return 0;
            return (completed[index] * 100) / item;
          }),
        },
      ],
    };

    const checklistsOnTimeChart = {
      labels: chartLabels,
      datasets: [
        {
          label: "Completed on time",
          backgroundColor: colors.theme["success"],
          data: completedOnTime,
          maxBarThickness: 20,
        },
        {
          label: "Completed late",
          backgroundColor: colors.theme["warning"],
          data: totalChecklists.map(
            (item, index) => item - completedOnTime[index]
          ),
          maxBarThickness: 20,
        },
      ],
    };

    const checklistsPassedChart = {
      labels: chartLabels,
      datasets: [
        {
          label: "Sales",
          data: totalChecklists.map((item, index) => {
            if (checklistsPassed[index] === 0) return 0;
            return (checklistsPassed[index] * 100) / item;
          }),
          maxBarThickness: 10,
        },
      ],
    };

    const customerSatisfactionChart = {
      labels: ["Satisfied", "Neutral", "Unsatisfied"],
      datasets: [
        {
          data: userFeedback,
          backgroundColor: [
            colors.theme["success"],
            colors.theme["warning"],
            colors.theme["danger"],
          ],
          label: "Customer Satisfaction",
        },
      ],
    }

    dispatch({
      type: types.SET_CHARTS,
      payload: {
        checklistsCompletedChart,
        checklistsOnTimeChart,
        checklistsPassedChart,
        customerSatisfactionChart,
        calendar: calendarFiltered || calendar,
      },
    });

    dispatch({
      type: types.SET_OVERALL_INFORMATION,
      payload: {
        totalChecklists: totalChecklists.filter(num => !isNaN(num)).reduce((sum, val) => (sum += val)),
        completedOnTime: completedOnTime.filter(num => !isNaN(num)).reduce((sum, val) => (sum += val)),
        checklistsPassed: checklistsPassed.filter(num => !isNaN(num)).reduce((sum, val) => (sum += val)),
        completed: completed.filter(num => !isNaN(num)).reduce((sum, val) => (sum += val)),
        additionalFeedback,
      },
    });
  } catch (error) {
    throw new Error(`Error creating data structure: ${error}`);
  }
};

export const getUsers = () => async (dispatch, getState) => {
  const userState = getState().user;
  const checklistsState = getState().checklists;
  dispatch({
    type: types.FETCHING_USERS,
  });
  try {
    const users = {};
    if (userState.user.role === "owner") {
      const franchises = Object.keys(checklistsState.franchises);
      franchises.forEach(franchise => {
        checklistsState.franchises[franchise].locations.forEach(location => {
          location.managers.forEach(manager => {
            users[manager] = null;
          });
        });
      });

      const usersSnapshots = await Promise.all(
        Object.keys(users).map(user => {
          return firebase.firestore().collection("users").doc(user).get();
        })
      );
      usersSnapshots.forEach(doc => {
        if (doc.exists) {
          users[doc.id] = doc.data();
        }
      });
    } else {
      const usersSnapshot = await firebase
        .firestore()
        .collection("users")
        .get();

      usersSnapshot.forEach(doc => {
        users[doc.id] = doc.data();
      });
    }

    dispatch({
      type: types.USERS_FETCHED,
      payload: users,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_FETCHING_USERS,
      payload: error,
    });
    throw new Error(`Error in getUsers: ${error}`);
  }
};

export const deleteLocation = (franchise, address) => async (dispatch, getState) => {
  dispatch({
    type: types.DELETING_FRANCHISE,
  });
  const location = getState().checklists.franchises[franchise].locations.filter(locationEl => locationEl.address === address)[0]
  try {
    // Delete Location Specific Checklists
    const dbRef = firebase
      .firestore()
      .collection('franchises')
      .doc(franchise)
      .collection('locations')
      .doc(address)

    location.locationSpecificChecklists.forEach(checklist => {
      dbRef
        .collection('locationSpecificChecklists')
        .doc(checklist.name)
        .delete()
    })
    // Delete past checklists
    const managersPastChecklistsSnapshot = await dbRef
      .collection('pastChecklistsManagers')
      .get()

      const techniciansPastChecklistsSnapshot = await dbRef
      .collection('pastChecklistsTechnicians')
      .get()

    managersPastChecklistsSnapshot.forEach(doc => {
      doc.ref.delete()
    });

    techniciansPastChecklistsSnapshot.forEach(doc => {
      doc.ref.delete()
    })

    dbRef.delete()

    dispatch({
      type: types.FRANCHISE_DELETED,
      payload: {
        franchise,
        address,
      }
    })

  } catch(error) {
    dispatch({
      type: types.ERROR_DELETING_FRANCHISE,
      payload: error.message,
    })
    throw new Error(`Error in deleteLocation: ${error}`);
  } 
}

export const createFranchise = franchise => async (dispatch, getState) => {
  const userState = getState().user;
  dispatch({
    type: types.CREATING_FRANCHISE,
  });

  try {
    const franchiseDoc = await firebase
      .firestore()
      .collection("franchises")
      .doc(franchise.name)
      .get();
    if (!franchiseDoc.exists) {
      await firebase
        .firestore()
        .collection("franchises")
        .doc(franchise.name)
        .set({ exists: true });
    }
    const locationDoc = await firebase
      .firestore()
      .collection("franchises")
      .doc(franchise.name)
      .collection("locations")
      .doc(franchise.address)
      .get();
    if (!locationDoc.exists) {
      await firebase
        .firestore()
        .collection("franchises")
        .doc(franchise.name)
        .collection("locations")
        .doc(franchise.address)
        .set({
          managers: [],
          owner: userState.user.uid,
          technicianAssigned: "",
          state: franchise.state,
        });
      dispatch({
        type: types.FRANCHISE_CREATED,
        payload: {
          managers: [],
          owner: userState.user.uid,
          technicianAssigned: "",
          state: franchise.state,
        }
      })
    } else if (locationDoc.data().owner === "") {
      await firebase
        .firestore()
        .collection("franchises")
        .doc(franchise.name)
        .collection("locations")
        .doc(franchise.address)
        .update({
          owner: userState.user.uid,
        });
      dispatch({
        type: types.FRANCHISE_CREATED,
        payload: {
          managers: [],
          owner: userState.user.uid,
          technicianAssigned: "",
          state: franchise.state,
        }
      })
    } else {
      dispatch({
        type: types.ERROR_CREATING_FRANCHISE,
        payload:
          "This franchise already exists and has been claimed by another user. Please verify the information.\nIf you are the owner of this location please contact us.",
      });
      console.error("it exists");
    }
  } catch (error) {
    dispatch({
      type: types.ERROR_CREATING_FRANCHISE,
      payload: error.message,
    });
    throw new Error(`Error in createFranchise: ${error}`);
  }
};

export const setFranchises = franchise => (dispatch, getState) => {
  const checklistsState = getState().checklists;
  const userState = getState().user;

  if (!checklistsState.franchises) {
    dispatch({
      type: types.SET_FRANCHISES,
      payload: {
        [franchise.name]: {
          locations: [
            {
              technicianAssigned: "",
              managers: [],
              owner: userState.uid,
              address: franchise.address,
              locationSpecificChecklists: [],
              state: franchise.state,
            },
          ],
          generalChecklists: [],
        },
      },
    });
  } else {
    const franchises = { ...checklistsState.franchises };
    if (franchises[franchise.name]) {
      franchises[franchise.name].locations.push({
        technicianAssigned: "",
        managers: [],
        owner: userState.uid,
        address: franchise.address,
        locationSpecificChecklists: [],
        state: franchise.state,
      });
      dispatch({
        type: types.SET_FRANCHISES,
        payload: franchises,
      });
    } else {
      dispatch({
        type: types.SET_FRANCHISES,
        payload: {
          ...checklistsState.franchises,
          [franchise.name]: {
            locations: [
              {
                technicianAssigned: "",
                managers: [],
                owner: userState.uid,
                address: franchise.address,
                locationSpecificChecklists: [],
                state: franchise.state,
              },
            ],
            generalChecklists: [],
          },
        },
      });
    }
  }
};

export const clearChecklists = () => dispatch => {
  dispatch({
    type: types.CLEAR_CHECKLIST,
  });
};

export const createChecklist = checklists => async dispatch => {
  dispatch({
    type: types.CREATING_CHECKLIST,
  });
  try {
    if (checklists.locations[0].location === "general") {
      await Promise.all(
        checklists.locations[0].franchise.map(
          async franchise =>
            await firebase
              .firestore()
              .collection("franchises")
              .doc(franchise)
              .collection("generalChecklists")
              .doc(checklists.name)
              .set(checklists.checklist)
        )
      );
      return dispatch({
        type: types.CHECKLIST_CREATED,
        payload: checklists,
      });
    }
    await Promise.all(
      checklists.locations.map(async location => {
        await firebase
          .firestore()
          .collection("franchises")
          .doc(location.franchise)
          .collection("locations")
          .doc(location.location)
          .collection("locationSpecificChecklists")
          .doc(checklists.name)
          .set(checklists.checklist);
      })
    );
    return dispatch({
      type: types.CHECKLIST_CREATED,
      payload: checklists,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_CREATING_CHECKLIST,
      payload: error.message,
    });
    throw new Error(`Error in createChecklist: ${error}`);
  }
};

export const deleteChecklist = prop => async dispatch => {
  dispatch({
    type: types.DELETING_CHECKLIST,
  });
  try {
    if (prop.type === "generalChecklists") {
      await firebase
        .firestore()
        .collection("franchises")
        .doc(prop.franchise)
        .collection(prop.type)
        .doc(prop.name)
        .delete();
    } else {
      await firebase
        .firestore()
        .collection("franchises")
        .doc(prop.franchise)
        .collection("locations")
        .doc(prop.location)
        .collection("locationSpecificChecklists")
        .doc(prop.name)
        .delete();
    }
    dispatch({
      type: types.CHECKLIST_DELETED,
      payload: prop,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_DELETING_CHECKLIST,
    });
    throw new Error(`Error in deleteChecklist: ${error}`);
  }
};

export const addUserLocation = user => async dispatch => {
  dispatch({
    type: types.ADDING_USER_LOCATION,
  });
  try {
    const batch = firebase.firestore().batch();
    const franchiseRef = firebase
      .firestore()
      .collection("franchises")
      .doc(user.franchise)
      .collection("locations")
      .doc(user.location);
    const userRef = firebase.firestore().collection("users").doc(user.uid);
    batch.update(
      userRef,
      { assignedLocations: user.locations },
      { merge: true }
    );
    batch.update(
      franchiseRef,
      user.technician
        ? { technicianAssigned: user.uid }
        : { managers: user.locationManagers },
      { merge: true }
    );
    await batch.commit();
    dispatch({
      type: types.USER_LOCATION_ADDED,
      payload: user,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_ADDING_USER_LOCATION,
      payload: error,
    });
    throw new Error(`Error in addUserLocation: ${error}`);
  }
};

export const removeUserLocation = user => async dispatch => {
  dispatch({
    type: types.REMOVING_USER_LOCATION,
  });
  try {
    const batch = firebase.firestore().batch();
    const franchiseRef = firebase
      .firestore()
      .collection("franchises")
      .doc(user.franchise)
      .collection("locations")
      .doc(user.location);
    const userRef = firebase.firestore().collection("users").doc(user.uid);
    batch.update(
      userRef,
      { assignedLocations: user.locations },
      { merge: true }
    );
    batch.update(
      franchiseRef,
      user.technician
        ? { technicianAssigned: user.uid }
        : { managers: user.locationManagers },
      { merge: true }
    );
    await batch.commit();
    dispatch({
      type: types.USER_LOCATION_REMOVED,
      payload: user,
    });
  } catch (error) {
    dispatch({
      type: types.ERROR_REMOVING_USER_LOCATION,
    });
    throw new Error(`Error in removeUserLocation: ${error}`);
  }
};
